import {authHeaders, baseUrl, handleError, handleResponse} from "./apiUtils";
import { persistAuthToken } from "./authTokenStorage";

async function parseJsonBody(response) {
  const rawResponse = await response.text();
  if (!rawResponse) {
    return null;
  }

  try {
    return JSON.parse(rawResponse);
  } catch (error) {
    return rawResponse;
  }
}

function finishAuthResponse(response) {
  if (!response || !response.token) {
    return response;
  }

  persistAuthToken(response.token);
  return response.user;
}

function signIn(user) {
  return fetch(`${baseUrl}login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(user),
  })
    .then(handleResponse)
    .then(finishAuthResponse)
    .catch(handleError);
}

async function signInWithGoogle(token) {
  const response = await fetch(`${baseUrl}login/google`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });

  const payload = await parseJsonBody(response);

  if (!response.ok) {
    const message = typeof payload === "string"
      ? payload
      : `Network response was not ok. status:${response.status}`;
    throw new Error(message);
  }

  return finishAuthResponse(payload);
}

function signUpWithPassword(payload) {
  return fetch(`${baseUrl}signup/password`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  })
    .then(handleResponse)
    .catch(handleError);
}

function confirmEmailVerification(token) {
  return fetch(`${baseUrl}email-verification/confirm`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  })
    .then(handleResponse)
    .catch(handleError);
}

function resendEmailVerification(email) {
  return fetch(`${baseUrl}email-verification/resend`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  })
    .then(handleResponse)
    .catch(handleError);
}

function selectLoginContext(contextChoiceToken, contextKey) {
  return fetch(`${baseUrl}login/context`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ contextChoiceToken, contextKey }),
  })
    .then(handleResponse)
    .then(finishAuthResponse)
    .catch(handleError);
}

function selectSuperAdminSchool(schoolChoiceToken, schoolId) {
  return fetch(`${baseUrl}login/school-context`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ schoolChoiceToken, schoolId }),
  })
    .then(handleResponse)
    .then(finishAuthResponse)
    .catch(handleError);
}

function getLoginContexts() {
  return fetch(`${baseUrl}login/contexts`, {
    method: "POST",
    headers: authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({}),
  })
    .then(handleResponse)
    .catch(handleError);
}

function completeSsoProfile(profileCompletionToken, details) {
  return fetch(`${baseUrl}signup/sso/complete-profile`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ profileCompletionToken, ...details }),
  })
    .then(handleResponse)
    .then(finishAuthResponse)
    .catch(handleError);
}

function requestPasswordReset(email) {
  return fetch(`${baseUrl}password-reset/request`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  })
    .then(handleResponse)
    .catch(handleError);
}

function confirmPasswordReset(token, password) {
  return fetch(`${baseUrl}password-reset/confirm`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token, password }),
  })
    .then(handleResponse)
    .catch(handleError);
}

export default {
  signIn,
  signInWithGoogle,
  signUpWithPassword,
  confirmEmailVerification,
  resendEmailVerification,
  selectLoginContext,
  selectSuperAdminSchool,
  getLoginContexts,
  completeSsoProfile,
  requestPasswordReset,
  confirmPasswordReset,
};
